"use client";

import { useTranslation } from "react-i18next";
import { getDaysInMonth, Months } from "@finbackoffice/fe-core";
import classNames from "classnames";
import { DropdownOption, useDidUpdateEffect } from "@finbackoffice/site-core";
import { FC, useCallback, useMemo, useState } from "react";
import { LEGAL_AGE } from "utils/constants";
import UniversalDeviceDropdown from "../universal-device-dropdown/UniversalDeviceDropdown";
import styles from "./date-picker.module.sass";

type IProps = {
    required?: boolean;
    disabled?: boolean;
    onChange: (val: number) => void;
    label: string;
    value: number | Date;
    wrapperClassname?: string;
    testId?: string;
    legal_age?: number;
};

const DatePicker: FC<IProps> = ({
    required = false,
    disabled = false,
    legal_age = LEGAL_AGE,
    onChange,
    label,
    wrapperClassname,
    value,
    testId = "",
}) => {
    const { t } = useTranslation();
    const [daysInMonth, setDaysInMonth] = useState<number>(getDaysInMonth());
    const dateInstance = useMemo(() => new Date(value), [value]);
    const dayValue = dateInstance.getDate();
    const monthValue = dateInstance.getMonth();
    const yearValue = dateInstance.getUTCFullYear();
    const [day, setDay] = useState(Number.isNaN(dayValue) ? "" : dayValue.toString());
    const [month, setMonth] = useState(Number.isNaN(monthValue) ? "" : monthValue.toString());
    const [year, setYear] = useState(Number.isNaN(yearValue) ? "" : yearValue.toString());
    const initialValue = !Number.isNaN(dayValue) ? dateInstance : null;

    useDidUpdateEffect(() => {
        if (day && month && year) {
            onChange(new Date(parseInt(year), parseInt(month), parseInt(day)).valueOf());
        }
    }, [day, month, year]);

    useDidUpdateEffect(() => {
        const isInvalidDate = Number.isNaN(dateInstance.getDate());
        const shouldResetToInitialValue =
            dateInstance.valueOf() === initialValue?.valueOf() &&
            initialValue.valueOf() !==
                new Date(parseInt(year), parseInt(month), parseInt(day)).valueOf();
        if (isInvalidDate && !initialValue) {
            setDay("");
            setMonth("");
            setYear("");
        } else if (shouldResetToInitialValue) {
            setDay(initialValue.getDate().toString());
            setMonth(initialValue.getMonth().toString());
            setYear(initialValue.getUTCFullYear().toString());
        }
    }, [dateInstance]);

    const getDays = useCallback(
        (amount: number): DropdownOption[] =>
            [...Array(amount).keys()].map((el) => ({
                value: (el + 1).toString(),
                label: (el + 1).toString(),
            })),
        [],
    );

    const months: DropdownOption[] = useMemo(
        () =>
            Months.map((item, index) => {
                return { value: index.toString(), label: item.number };
            }),
        [],
    );

    const years: DropdownOption[] = useMemo(() => {
        const now = new Date().getUTCFullYear();
        return Array(102)
            .fill("")
            .map((_val, i) => ({
                value: (now - i - legal_age).toString(),
                label: (now - i - legal_age).toString(),
            }));
    }, [legal_age]);

    const updateDay = useCallback((val: string) => {
        setDay(val);
    }, []);

    const updateMonth = useCallback(
        (val: string) => {
            setDaysInMonth(getDaysInMonth(parseInt(val) + 1, parseInt(year)));
            setMonth(val);
        },
        [year],
    );

    const updateYear = useCallback(
        (val: string) => {
            setDaysInMonth(getDaysInMonth(parseInt(val) + 1, parseInt(year)));
            setYear(val);
        },
        [year],
    );

    return (
        <div className={classNames(styles.datePicker, wrapperClassname)}>
            <UniversalDeviceDropdown
                name="day"
                options={getDays(daysInMonth)}
                value={day}
                onChange={updateDay}
                wrapperClassname={styles.dropdown}
                label={label}
                placeholder={t("userData_dd")}
                disabled={disabled}
                testId={`${testId}-day`}
            />
            <UniversalDeviceDropdown
                name="month"
                options={months}
                value={month}
                onChange={updateMonth}
                wrapperClassname={styles.dropdown}
                label={label}
                placeholder={t("userData_mm")}
                disabled={disabled}
                testId={`${testId}-month`}
            />
            <UniversalDeviceDropdown
                name="year"
                options={years}
                value={year}
                onChange={updateYear}
                wrapperClassname={styles.dropdown}
                label={label}
                placeholder={t("userData_yy")}
                disabled={disabled}
                required={required}
                testId={`${testId}-year`}
            />
        </div>
    );
};
export default DatePicker;
